import { useTranslation } from 'react-i18next';
import { useDebounceCallback } from 'usehooks-ts';

import {
  Button,
  Flex,
  Icon,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';
import { EditableTitle } from '@/shared/components/EditableTitle';
import { DISPLAY_NAME_MAX_LENGTH, UI_ACTION_DEBOUNCE_MS } from '@/shared/utils';

export interface IThreadOption {
  value: string;
  label: string;
  /** Short activity label (time / relative / date) for the dropdown row. */
  description: string;
}

interface IThreadSelectorProps {
  /** Dropdown options, already built + disambiguated by the caller. */
  options: IThreadOption[];
  /** Currently selected thread id (controlled by the caller). */
  selectedThreadId?: string;
  /** Called when the user selects an existing thread. */
  onSelectThread: (threadId: string) => void;
  /** Fires when the dropdown opens, e.g. to refetch the latest thread list. */
  onOpen?: () => void;
  /** Shows a spinner in the dropdown header while the option list refetches. */
  isLoadingOptions?: boolean;
  /** Called when the user creates a new thread. Debounced internally. */
  onCreateThread: () => void;
  /** Called when the user renames the selected thread. */
  onRenameThread: (displayName: string) => Promise<void>;
  /** Disables the select, rename field, and create button while creating. */
  isCreating?: boolean;
  /** Shows a spinner on the create button (e.g. while creating/refetching). */
  isCreateLoading?: boolean;
  /** Distinguishes the Quill / Rain instances in tests. */
  dataTestId: string;
}

/**
 * Presentational thread switcher shared by the Quill and Rain chat selectors.
 * Purely visual: the caller owns data fetching and builds `options`; this
 * renders the history dropdown, inline-rename title, and new-thread button.
 */
export function ThreadSelector({
  options,
  selectedThreadId,
  onSelectThread,
  onOpen,
  isLoadingOptions = false,
  onCreateThread,
  onRenameThread,
  isCreating = false,
  isCreateLoading = false,
  dataTestId,
}: IThreadSelectorProps) {
  const { t } = useTranslation('automation');

  const selectedLabel = options.find(
    option => option.value === selectedThreadId,
  )?.label;

  const debouncedCreateThread = useDebounceCallback(
    onCreateThread,
    UI_ACTION_DEBOUNCE_MS,
    { leading: true, trailing: false },
  );

  return (
    <Flex
      align="center"
      gap={2}
      width="full"
      paddingX={3}
      paddingY={2}
      data-testid={dataTestId}
    >
      {options.length > 0 ? (
        <Flex
          align="center"
          paddingY={0.5}
          className="h-9 max-w-[300px] min-w-0 rounded-lg border border-border pr-1 pl-1"
        >
          <Select
            value={selectedThreadId}
            onValueChange={onSelectThread}
            onOpenChange={open => {
              if (open) onOpen?.();
            }}
            disabled={isCreating}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <SelectTrigger
                  size="sm"
                  aria-label={t('automation.quillChat.thread.recentChats')}
                  className="size-7 shrink-0 justify-center rounded-md border-0 bg-transparent p-0 shadow-none data-[size=sm]:h-7 not-disabled:hover:bg-accent not-disabled:hover:text-accent-foreground [&>svg:last-child]:hidden"
                >
                  <Icon type="MessageCircle" className="text-ring" />
                </SelectTrigger>
              </TooltipTrigger>
              <TooltipContent>
                {t('automation.quillChat.thread.recentChats')}
              </TooltipContent>
            </Tooltip>
            <SelectContent
              align="start"
              // Anchor is the icon trigger, inset by the wrapper's border +
              // pl-0.5; pull left so the popover aligns to the bordered box.
              alignOffset={-3}
              // Hide Radix's arrow scroll buttons; overflow scrolls by
              // wheel/trackpad instead.
              className="max-h-[400px] w-[300px] [&_[data-slot=select-scroll-up-button]]:hidden [&_[data-slot=select-scroll-down-button]]:hidden"
            >
              <SelectGroup>
                <SelectLabel>
                  <Flex align="center" gap={2}>
                    {t('automation.quillChat.thread.recentChats')}
                    {isLoadingOptions && (
                      <Icon
                        type="Loader2"
                        className="size-3 animate-spin text-muted-foreground"
                      />
                    )}
                  </Flex>
                </SelectLabel>
                {options.map(option => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className="[&>span:last-child]:flex [&>span:last-child]:min-w-0"
                  >
                    <Flex
                      direction="column"
                      align="stretch"
                      gap={0}
                      className="w-full min-w-0"
                    >
                      <span
                        className="truncate font-medium"
                        data-testid="thread-option-label"
                      >
                        {option.label}
                      </span>
                      {option.description && (
                        <span className="text-muted-foreground truncate text-xs">
                          {option.description}
                        </span>
                      )}
                    </Flex>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <EditableTitle
            value={selectedLabel ?? ''}
            onRename={onRenameThread}
            isEditable={Boolean(selectedThreadId)}
            isDisabled={isCreating}
            level="small"
            weight="medium"
            className="h-full min-w-0 max-w-full"
            maxLength={DISPLAY_NAME_MAX_LENGTH}
            placeholder={t('automation.quillChat.thread.renamePlaceholder')}
          />
        </Flex>
      ) : (
        <Flex align="center" gap={2} className="min-w-0 flex-1 px-2">
          <Icon type="MessageCircle" className="size-4 text-muted-foreground" />
          <Text level="small" color="muted">
            {t('automation.quillChat.thread.empty')}
          </Text>
        </Flex>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="icon"
            variant="outline"
            onClick={debouncedCreateThread}
            disabled={isCreating || isCreateLoading}
            aria-label={t('automation.quillChat.thread.new')}
            className="ml-auto shrink-0 bg-transparent shadow-none"
          >
            <Icon type="Plus" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('automation.quillChat.thread.new')}</TooltipContent>
      </Tooltip>
    </Flex>
  );
}
